import type React from 'react';
import { useEffect, useState } from 'react';
import './LandingPage.css';
import { API_CONFIG } from '../config/api';
import { clearEmployeeNotFoundFlag, hasEmployeeNotFoundFlag } from '../contexts/AuthContext';
import { getOAuthConfig } from '../config/oauthConfig';
import { logger } from '../utils/logger';
import { handleApiError } from '../lib/errors';

const LandingPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showEmployeeNotFoundMessage, setShowEmployeeNotFoundMessage] = useState(false);

  useEffect(() => {
    if (hasEmployeeNotFoundFlag()) {
      setShowEmployeeNotFoundMessage(true);
    }
  }, []);

  const handleTryAgain = () => {
    clearEmployeeNotFoundFlag();
    setShowEmployeeNotFoundMessage(false);
  };

  const handleLogin = async () => {
    setIsLoading(true);

    try {
      const oauthConfig = await getOAuthConfig();
      const { tenantId, clientId, redirectUri } = oauthConfig;

      // Validate required fields
      if (!tenantId) {
        logger.error('ERROR: Tenant ID is not configured!');
        alert('Tenant ID is not configured. Please contact support.');
        setIsLoading(false);
        return;
      }

      if (!clientId) {
        logger.error('ERROR: Microsoft OAuth Client ID is not configured!');
        alert('Client ID is not configured. Please contact support.');
        setIsLoading(false);
        return;
      }

      if (!redirectUri) {
        logger.error('ERROR: Redirect URI is not configured!');
        alert('Redirect URI is not configured. Please contact support.');
        setIsLoading(false);
        return;
      }

      // Validate redirect URI - must not contain localhost in production
      const isDevelopment =
        API_CONFIG.baseURL.includes('localhost') || API_CONFIG.baseURL.includes('127.0.0.1');
      if (
        !isDevelopment &&
        (redirectUri.includes('localhost') || redirectUri.includes('127.0.0.1'))
      ) {
        logger.error('ERROR: Redirect URI contains localhost:', redirectUri);
        alert(
          'Invalid redirect URI configuration. Redirect URI cannot contain localhost in production.'
        );
        setIsLoading(false);
        return;
      }

      logger.debug('OAuth Configuration loaded:', {
        tenantId: tenantId,
        clientId: clientId ? `${clientId.substring(0, 8)}...` : 'NOT SET',
        redirectUri: redirectUri,
      });

      // Build OAuth URL with required parameters
      const encodedRedirectUri = encodeURIComponent(redirectUri);
      const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodedRedirectUri}&scope=openid%20profile&state=1234`;

      // Redirect to Microsoft OAuth login
      window.location.href = authUrl;
    } catch (error) {
      logger.error('Failed to load OAuth configuration:', error);
      const errorMessage = handleApiError(error);
      alert(errorMessage || 'Failed to load authentication configuration. Please contact support.');
      setIsLoading(false);
    }
  };

  return (
    <div className="landing-page">
      <div className="landing-container">
        <div className="landing-page-logo">
          <img src="/assets/logo/PPE-intelligence_Logo_rgb_Black.svg" alt="PPE Intelligence Logo" />
        </div>
        <div className="landing-content">
          <h1 className="landing-title">Time Tracking Tool</h1>
          {showEmployeeNotFoundMessage ? (
            <>
              <div
                style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                }}
              >
                <p
                  style={{
                    color: '#856404',
                    margin: '0 0 1rem 0',
                    fontSize: '1rem',
                    lineHeight: '1.5',
                  }}
                >
                  Your account is not registered in the system. Please contact support to request
                  access.
                </p>
                <button
                  onClick={() => (window.location.href = '/contact-support')}
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0.5rem 1.5rem',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    marginRight: '0.5rem',
                  }}
                >
                  Contact Support
                </button>
                <button
                  onClick={handleTryAgain}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#856404',
                    border: '1px solid #856404',
                    borderRadius: '4px',
                    padding: '0.5rem 1.5rem',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                  }}
                >
                  Try Again
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="landing-subtitle">Sign in to access your time tracking dashboard</p>
              <button
                className="login-button"
                onClick={handleLogin}
                disabled={isLoading}
                aria-label="Sign in with Microsoft"
              >
                {isLoading ? (
                  <span>Loading...</span>
                ) : (
                  <>
                    <svg
                      className="microsoft-icon"
                      viewBox="0 0 23 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="0" y="0" width="11" height="11" fill="#F25022" />
                      <rect x="12" y="0" width="11" height="11" fill="#7FBA00" />
                      <rect x="0" y="12" width="11" height="11" fill="#00A4EF" />
                      <rect x="12" y="12" width="11" height="11" fill="#FFB900" />
                    </svg>
                    Sign in with Microsoft
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
